import { call, put, select, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { shipToApi, ShipTo, ShipToSearchParams } from '../../services/shipToApi';
import { RootState } from '../reducers';
import {
  setLoading, setError, setItems, setSelectedItem, addItem, updateItem, removeItem,
  setValidationErrors, setPagination, setBulkLoading, setSuccessMessage,
  FETCH_SHIPTO, FETCH_SHIPTO_BY_ID, CREATE_SHIPTO, UPDATE_SHIPTO, DELETE_SHIPTO,
  SEARCH_SHIPTO, BULK_ACTIVATE_SHIPTO, BULK_DEACTIVATE_SHIPTO, BULK_DELETE_SHIPTO,
  FETCH_SHIPTO_STATS, EXPORT_SHIPTO_CSV
} from '../reducers/shipToSlice';

function* fetchShipToSaga(action: PayloadAction<ShipToSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true)); yield put(setError(null));
    const state: RootState = yield select();
    const { pagination, searchParams } = state.shipTo;
    const params = { page: pagination.page, pageSize: pagination.pageSize, ...searchParams, ...action.payload };
    const resp = yield call(shipToApi.getAll, params);
    yield put(setItems(resp.data));
    yield put(setPagination({ total: resp.total, page: params.page || 1, pageSize: params.pageSize || 10 }));
  } catch (e: any) { yield put(setError(e.message || 'Failed to fetch Ship-To items')); }
  finally { yield put(setLoading(false)); }
}

function* fetchShipToByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const item = yield call(shipToApi.getById, action.payload); yield put(setSelectedItem(item)); }
  catch (e: any) { yield put(setError(e.message || 'Failed to fetch Ship-To item')); }
  finally { yield put(setLoading(false)); }
}

function* createShipToSaga(action: PayloadAction<Omit<ShipTo, 'Id'>>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const errors = yield call(shipToApi.validation.validate, action.payload); if (errors.length) { yield put(setValidationErrors(errors)); return; } const created = yield call(shipToApi.create, action.payload); yield put(addItem(created)); yield put(setSuccessMessage('Created successfully')); }
  catch (e: any) { yield put(setError(e.message || 'Failed to create Ship-To item')); }
  finally { yield put(setLoading(false)); }
}

function* updateShipToSaga(action: PayloadAction<{ id: number; item: Partial<ShipTo> }>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const { id, item } = action.payload; const errors = yield call(shipToApi.validation.validate, item); if (errors.length) { yield put(setValidationErrors(errors)); return; } const updated = yield call(shipToApi.update, id, item); yield put(updateItem(updated)); yield put(setSuccessMessage('Updated successfully')); }
  catch (e: any) { yield put(setError(e.message || 'Failed to update Ship-To item')); }
  finally { yield put(setLoading(false)); }
}

function* deleteShipToSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const resp = yield call(shipToApi.delete, action.payload); if (resp.success) { yield put(removeItem(action.payload)); yield put(setSuccessMessage('Deleted successfully')); } else { yield put(setError(resp.message || 'Failed to delete')); } }
  catch (e: any) { yield put(setError(e.message || 'Failed to delete Ship-To item')); }
  finally { yield put(setLoading(false)); }
}

function* searchShipToSaga(action: PayloadAction<ShipToSearchParams>): Generator<any, void, any> {
  try { yield put(setLoading(true)); yield put(setError(null)); const resp = yield call(shipToApi.search, action.payload); yield put(setItems(resp.data)); yield put(setPagination({ total: resp.total, page: action.payload.page || 1, pageSize: action.payload.pageSize || 10 })); }
  catch (e: any) { yield put(setError(e.message || 'Failed to search Ship-To items')); }
  finally { yield put(setLoading(false)); }
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkLoading(true)); const resp = yield call(shipToApi.bulkActivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Activated successfully')); yield put({ type: FETCH_SHIPTO }); } else { yield put(setError(resp.message || 'Failed to activate')); } }
  finally { yield put(setBulkLoading(false)); }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkLoading(true)); const resp = yield call(shipToApi.bulkDeactivate, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deactivated successfully')); yield put({ type: FETCH_SHIPTO }); } else { yield put(setError(resp.message || 'Failed to deactivate')); } }
  finally { yield put(setBulkLoading(false)); }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try { yield put(setBulkLoading(true)); const resp = yield call(shipToApi.bulkDelete, action.payload); if (resp.success) { yield put(setSuccessMessage(resp.message || 'Deleted successfully')); yield put({ type: FETCH_SHIPTO }); } else { yield put(setError(resp.message || 'Failed to delete')); } }
  finally { yield put(setBulkLoading(false)); }
}

function* exportCsvSaga(action: PayloadAction<ShipToSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    const state: RootState = yield select();
    const params = { ...state.shipTo.searchParams, ...action.payload };
    const blob = yield call(shipToApi.exportToCSV, params);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `shipto_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); window.URL.revokeObjectURL(url);
    yield put(setSuccessMessage('Exported successfully'));
  } catch (e: any) { yield put(setError(e.message || 'Failed to export Ship-To items')); }
  finally { yield put(setLoading(false)); }
}

export function* shipToSaga(): Generator<any, void, any> {
  yield takeEvery(FETCH_SHIPTO, fetchShipToSaga);
  yield takeEvery(FETCH_SHIPTO_BY_ID, fetchShipToByIdSaga);
  yield takeEvery(CREATE_SHIPTO, createShipToSaga);
  yield takeEvery(UPDATE_SHIPTO, updateShipToSaga);
  yield takeEvery(DELETE_SHIPTO, deleteShipToSaga);
  yield takeEvery(SEARCH_SHIPTO, searchShipToSaga);
  yield takeEvery(BULK_ACTIVATE_SHIPTO, bulkActivateSaga);
  yield takeEvery(BULK_DEACTIVATE_SHIPTO, bulkDeactivateSaga);
  yield takeEvery(BULK_DELETE_SHIPTO, bulkDeleteSaga);
  yield takeEvery(EXPORT_SHIPTO_CSV, exportCsvSaga);
} 